import {
  Mesh,
  MeshStandardMaterial,
  Group,
  Vector3,
  Color,
  BufferGeometry,
  BufferAttribute,
} from 'three';
import {
  TILE_SIZE,
  WORLD_SCALE,
  MAX_HEIGHT,
  TILE_WORLD,
  LOAD_RADIUS,
  UNLOAD_RADIUS,
  LOD_DISTANCES,
  LOD_SEGMENTS,
  NORMAL_DELTA,
} from './config';

function terrainColor(relH: number): Color {
  const stops = [
    { t: 0.0, r: 0.16, g: 0.18, b: 0.42 },
    { t: 0.25, r: 0.16, g: 0.40, b: 0.22 },
    { t: 0.45, r: 0.35, g: 0.55, b: 0.22 },
    { t: 0.65, r: 0.53, g: 0.38, b: 0.23 },
    { t: 0.85, r: 0.74, g: 0.70, b: 0.66 },
    { t: 1.0, r: 0.98, g: 0.98, b: 0.98 },
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (relH <= b.t) {
      const f = (relH - a.t) / (b.t - a.t);
      return new Color(
        a.r + (b.r - a.r) * f,
        a.g + (b.g - a.g) * f,
        a.b + (b.b - a.b) * f,
      );
    }
  }
  return new Color(stops[stops.length - 1].r, stops[stops.length - 1].g, stops[stops.length - 1].b);
}

export interface TileData {
  tx: number;
  ty: number;
  width: number;
  height: number;
  data: Uint16Array;
}

export interface TileEntry {
  tx: number;   // wrapped data index [0, tilesX)  (for heightmap sampling + key)
  ty: number;
  utx: number;  // unwrapped tile coord (for render position + unload distance)
  uty: number;
  data: TileData | null;
  mesh: Mesh | null;
  lodLevel: number;
  loading: boolean;
}

const BUILD_BUDGET = 4;

export class ChunkManager {
  public group = new Group();
  private tiles = new Map<string, TileEntry>();
  public tilesX = 0;
  public tilesY = 0;
  public fullHeightmap: Float32Array | null = null;
  public fullW = 0;
  public fullH = 0;

  // Origin rebasing: keep GPU coords near (0,0,0)
  private centerTX = 0;
  private centerTY = 0;
  public worldOrigin = new Vector3();

  private terrainMaterial: MeshStandardMaterial;
  private pending: TileEntry[] = [];
  private builtCount = 0;
  private colorHSL = new Color();

  constructor() {
    this.terrainMaterial = new MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.9,
      metalness: 0.0,
      flatShading: false,
    });
  }

  async loadFullHeightmap(): Promise<void> {
    const resp = await fetch('./assets/heightmap.png');
    const blob = await resp.blob();
    const bmp = await createImageBitmap(blob);
    const canvas = new OffscreenCanvas(bmp.width, bmp.height);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bmp, 0, 0);
    const imgData = ctx.getImageData(0, 0, bmp.width, bmp.height);

    this.fullW = bmp.width;
    this.fullH = bmp.height;
    this.tilesX = Math.ceil(bmp.width / TILE_SIZE);
    this.tilesY = Math.ceil(bmp.height / TILE_SIZE);
    this.fullHeightmap = new Float32Array(bmp.width * bmp.height);

    for (let i = 0; i < bmp.width * bmp.height; i++) {
      const v = imgData.data[i * 4];
      this.fullHeightmap[i] = v / 255;
    }
  }

  get totalTilesNeeded(): number {
    return (LOAD_RADIUS * 2 + 1) ** 2;
  }

  get tilesBuilt(): number {
    return this.builtCount;
  }

  private tileKey(tx: number, ty: number): string {
    return `${tx}_${ty}`;
  }

  private ensureTile(tx: number, ty: number): TileEntry {
    const key = this.tileKey(tx, ty);
    let entry = this.tiles.get(key);
    if (!entry) {
      entry = { tx, ty, utx: tx, uty: ty, data: null, mesh: null, lodLevel: -1, loading: false };
      this.tiles.set(key, entry);
    }
    return entry;
  }

  sampleHeight(wx: number, wz: number): number {
    if (!this.fullHeightmap) return 0;
    const w = this.fullW;
    const h = this.fullH;
    const px = wx / WORLD_SCALE;
    const pz = wz / WORLD_SCALE;

    let ix = ((px % w) + w) % w;
    let iy = ((pz % h) + h) % h;
    const x0 = Math.floor(ix);
    const y0 = Math.floor(iy);
    const x1 = (x0 + 1) % w;
    const y1 = (y0 + 1) % h;
    const fx = ix - x0;
    const fy = iy - y0;
    const v00 = this.fullHeightmap[y0 * w + x0];
    const v10 = this.fullHeightmap[y0 * w + x1];
    const v01 = this.fullHeightmap[y1 * w + x0];
    const v11 = this.fullHeightmap[y1 * w + x1];
    return ((v00 * (1 - fx) + v10 * fx) * (1 - fy) +
            (v01 * (1 - fx) + v11 * fx) * fy) * MAX_HEIGHT;
  }

  sampleNormal(wx: number, wz: number): Vector3 {
    const d = NORMAL_DELTA;
    const hL = this.sampleHeight(wx - d, wz);
    const hR = this.sampleHeight(wx + d, wz);
    const hD = this.sampleHeight(wx, wz - d);
    const hU = this.sampleHeight(wx, wz + d);
    return new Vector3(hL - hR, d * 2, hD - hU).normalize();
  }

  private getLODLevel(distInTiles: number): number {
    for (let i = LOD_DISTANCES.length - 1; i >= 0; i--) {
      if (distInTiles >= LOD_DISTANCES[i]) return i;
    }
    return 0;
  }

  // Build mesh with vertices in local space (0..TILE_WORLD), centered
  private buildMesh(tx: number, ty: number, lodLevel: number): Mesh {
    const segments = LOD_SEGMENTS[lodLevel];
    const size = TILE_WORLD;
    const vertCount = segments + 1;
    const totalVerts = vertCount * vertCount;

    // Absolute world origin of this tile's top-left corner
    const absX0 = tx * TILE_SIZE * WORLD_SCALE;
    const absZ0 = ty * TILE_SIZE * WORLD_SCALE;

    const positions = new Float32Array(totalVerts * 3);
    const colors = new Float32Array(totalVerts * 3);
    const indices: number[] = [];

    for (let iz = 0; iz < vertCount; iz++) {
      for (let ix = 0; ix < vertCount; ix++) {
        const idx = iz * vertCount + ix;
        const wx = absX0 + (ix / segments) * size;
        const wz = absZ0 + (iz / segments) * size;
        const h = this.sampleHeight(wx, wz);

        // Vertex relative to tile center
        positions[idx * 3] = wx - absX0 - size / 2;
        positions[idx * 3 + 1] = h;
        positions[idx * 3 + 2] = wz - absZ0 - size / 2;

        const relH = h / MAX_HEIGHT;
        const color = terrainColor(relH);
        colors[idx * 3] = color.r;
        colors[idx * 3 + 1] = color.g;
        colors[idx * 3 + 2] = color.b;
      }
    }

    for (let iz = 0; iz < segments; iz++) {
      for (let ix = 0; ix < segments; ix++) {
        const a = iz * vertCount + ix;
        const b = a + 1;
        const c = a + vertCount;
        const d = c + 1;
        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(positions, 3));
    geo.setAttribute('color', new BufferAttribute(colors, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();

    const mesh = new Mesh(geo, this.terrainMaterial);
    mesh.receiveShadow = true;
    return mesh;
  }

  // Position a tile relative to the current center, using its UNWRAPPED coord.
  // (tx - centerTX) would be wrong near the toroidal seam; utx keeps the true
  // small offset so tiles never teleport across the world.
  private repositionMesh(entry: TileEntry): void {
    if (!entry.mesh) return;
    entry.mesh.position.set(
      (entry.utx - this.centerTX + 0.5) * TILE_WORLD,
      0,
      (entry.uty - this.centerTY + 0.5) * TILE_WORLD,
    );
  }

  private repositionAll(): void {
    for (const entry of this.tiles.values()) {
      this.repositionMesh(entry);
    }
  }

  update(playerWorldX: number, playerWorldZ: number): number {
    if (!this.fullHeightmap) return 0;

    const playerTX = Math.floor(playerWorldX / TILE_WORLD);
    const playerTY = Math.floor(playerWorldZ / TILE_WORLD);

    // Origin rebase: if player moved to a different tile, recenter
    if (playerTX !== this.centerTX || playerTY !== this.centerTY) {
      this.worldOrigin.set(playerTX * TILE_WORLD, 0, playerTY * TILE_WORLD);
      this.centerTX = playerTX;
      this.centerTY = playerTY;
      this.repositionAll();
    }

    const desiredTiles = new Set<string>();

    // Process pending tile builds (frame budget)
    for (let n = 0; n < BUILD_BUDGET && this.pending.length > 0; ) {
      const entry = this.pending.shift()!;
      const dist = Math.max(
        Math.abs(entry.utx - playerTX),
        Math.abs(entry.uty - playerTY),
      );
      if (dist > UNLOAD_RADIUS) continue;
      if (entry.mesh) {
        this.group.remove(entry.mesh);
        entry.mesh.geometry.dispose();
        entry.mesh = null;
      }
      entry.mesh = this.buildMesh(entry.tx, entry.ty, entry.lodLevel);
      this.repositionMesh(entry);
      this.group.add(entry.mesh);
      this.builtCount++;
      n++;
    }

    for (let dty = -LOAD_RADIUS; dty <= LOAD_RADIUS; dty++) {
      for (let dtx = -LOAD_RADIUS; dtx <= LOAD_RADIUS; dtx++) {
        const utx = playerTX + dtx;
        const uty = playerTY + dty;
        const tx = ((utx % this.tilesX) + this.tilesX) % this.tilesX;
        const ty = ((uty % this.tilesY) + this.tilesY) % this.tilesY;
        const key = this.tileKey(tx, ty);
        desiredTiles.add(key);

        const entry = this.ensureTile(tx, ty);
        entry.utx = utx;
        entry.uty = uty;
        const distInTiles = Math.max(Math.abs(dtx), Math.abs(dty));
        const neededLOD = this.getLODLevel(distInTiles);

        if (entry.lodLevel !== neededLOD) {
          entry.lodLevel = neededLOD;
          if (!this.pending.includes(entry)) {
            this.pending.push(entry);
          }
        }
      }
    }

    // Unload tiles outside unload radius (by unwrapped distance)
    for (const [key, entry] of this.tiles) {
      const dist = Math.max(
        Math.abs(entry.utx - playerTX),
        Math.abs(entry.uty - playerTY),
      );
      if (dist > UNLOAD_RADIUS) {
        if (entry.mesh) {
          this.group.remove(entry.mesh);
          entry.mesh.geometry.dispose();
          entry.mesh = null;
        }
        entry.lodLevel = -1;
        entry.data = null;
        this.builtCount = Math.max(0, this.builtCount - 1);
        const pi = this.pending.indexOf(entry);
        if (pi !== -1) this.pending.splice(pi, 1);
      }
    }

    return desiredTiles.size;
  }

  getWorldWidth(): number {
    return this.tilesX * TILE_SIZE * WORLD_SCALE;
  }

  getWorldHeight(): number {
    return this.tilesY * TILE_SIZE * WORLD_SCALE;
  }
}